
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Location } from 'vue-router';
import * as ContentServices from '@/services/ContentServices';
import pageSchemas from '@/content/page-schemas.generated.json';
import { isRecord, PageDefinition } from '@/content/schema';
import { mediaUrl } from '@/content/pages';

interface FoundStory {
  seriesTitle: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  editLink: Location;
}

/** `/the-bigger-picture/<series>` (or a story address under it) → the series' slug, as the site reads it. */
function seriesSlugOf(href: string): string {
  const match = /^\/the-bigger-picture\/([a-z0-9-]+)/.exec(href);
  return match ? match[1] : '';
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * For a homepage card that follows a series: which story it shows now (the newest of the series its
 * overview link points to), with a link to that story's card in The bigger picture, where it is edited.
 */
@Component
export default class FollowedStory extends Vue {
  /** The overview link of the card, or its own link when that is empty. */
  @Prop({ required: true }) readonly href!: string;

  loading = false;
  series: unknown[] = [];
  mediaUrl = mediaUrl;

  get found(): FoundStory | null {
    const slug = seriesSlugOf(this.href);
    const index = this.series.findIndex((series) => isRecord(series) && series.slug === slug);
    const series = this.series[index];
    if (!slug || !isRecord(series) || !Array.isArray(series.items) || !isRecord(series.items[0])) return null;
    const story = series.items[0];
    const card = isRecord(story.card) ? story.card : {};
    return {
      seriesTitle: text(series.title),
      title: text(card.title),
      subtitle: text(card.subtitle),
      imageUrl: text(card.imageUrl),
      // Paths of the editor are 1-based, like its validation.
      editLink: { path: '/content', query: { pagina: 'the-bigger-picture', pad: `series[${index + 1}].items[1]` } },
    };
  }

  @Watch('href', { immediate: true })
  async load(): Promise<void> {
    if (this.series.length) return;
    this.loading = true;
    try {
      const result = await ContentServices.GetContent('the-bigger-picture');
      // Never saved: the site shows its defaults, so this does too.
      const fallback = (pageSchemas as Record<string, PageDefinition>)['the-bigger-picture'].defaults;
      const content = result && result.content ? result.content : fallback;
      this.series = isRecord(content) && Array.isArray(content.series) ? content.series : [];
    } catch (error) {
      this.series = [];
    }
    this.loading = false;
  }
}
